import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumbs from "../../Breadcrumbs";
import PaginationComponent from "../common/PaginationComponent";
import "../common/TextEditor.css";
import { getShowingRange } from "../../features/utils/paginationUtils";
import { fetchDoneTaskTrackerList } from "../../features/taskTrackerSlice/taskTrackerSlice";

const STATUS_OPTIONS = [
  { value: "1", label: "Pending" },
  { value: "2", label: "Done" },
  { value: "3", label: "Hold" },
  { value: "4", label: "Ongoing" },
  { value: "5", label: "Point not clear" },
  { value: "6", label: "Need approval" },
];

const STATUS_BY_VALUE = STATUS_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label;
  acc[Number(opt.value)] = opt.label;
  return acc;
}, {});

const STATUS_BADGE_CLASSES = {
  1: "bg-warning-subtle text-warning",
  2: "bg-success-subtle text-success",
  3: "bg-secondary-subtle text-secondary",
  4: "bg-info-subtle text-info",
  5: "bg-danger-subtle text-danger",
  6: "bg-primary-subtle text-primary",
};

const PRIORITY_BY_VALUE = {
  1: "Low",
  2: "Medium",
  3: "High",
  4: "Urgent",
};

const PRIORITY_BADGE_CLASSES = {
  1: "bg-secondary-subtle text-secondary",
  2: "bg-info-subtle text-info",
  3: "bg-warning-subtle text-warning",
  4: "bg-danger-subtle text-danger",
};

const TASKS_PER_PAGE = 10;
const uploadUrl = process.env.REACT_APP_BACKEND_UPLOADS;

const DoneTaskList = () => {
  const dispatch = useDispatch();
  const { doneList, doneListStatus, donePagination } = useSelector(
    (state) => state.taskTracker
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedTaskId, setExpandedTaskId] = useState(null);

  useEffect(() => {
    dispatch(
      fetchDoneTaskTrackerList({ page: currentPage, limit: TASKS_PER_PAGE })
    );
  }, [dispatch, currentPage]);

  const getImageUrl = (fileName) => {
    if (!fileName) return "";
    if (uploadUrl) return `${uploadUrl}/${fileName}`;
    return fileName;
  };

  const getStatusLabel = (item) =>
    item?.show_status ||
    STATUS_BY_VALUE[item?.status] ||
    STATUS_BY_VALUE[String(item?.status)] ||
    "Pending";

  const getStatusBadgeClass = (item) => {
    const statusKey = item?.status ?? 1;
    return (
      STATUS_BADGE_CLASSES[statusKey] ||
      STATUS_BADGE_CLASSES[String(statusKey)] ||
      STATUS_BADGE_CLASSES[1]
    );
  };

  const getPriorityLabel = (item) =>
    item?.show_priority ||
    PRIORITY_BY_VALUE[item?.priority] ||
    PRIORITY_BY_VALUE[String(item?.priority)] ||
    "Medium";

  const getPriorityBadgeClass = (item) => {
    const priorityKey = item?.priority ?? 2;
    return (
      PRIORITY_BADGE_CLASSES[priorityKey] ||
      PRIORITY_BADGE_CLASSES[String(priorityKey)] ||
      PRIORITY_BADGE_CLASSES[2]
    );
  };

  const toggleComments = (taskId) => {
    setExpandedTaskId((prev) => (prev === taskId ? null : taskId));
  };

  const formatCommentDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };

  const totalItems = donePagination?.totalItems || 0;
  const totalPages = donePagination?.totalPages || 1;
  const { start, end } = getShowingRange(currentPage, TASKS_PER_PAGE, totalItems);

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">Done Tasks</h4>
            <div className="page-title-right">
              <Breadcrumbs />
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between">
              <h5 className="card-title mb-0">Completed Task List</h5>
              <Link to="/task-tracker" className="btn btn-sm btn-primary">
                Back to Task Tracker
              </Link>
            </div>
            <div className="card-body">
              {doneListStatus === "loading" ? (
                <p className="text-center text-muted mb-0">
                  Loading done tasks...
                </p>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-bordered table-striped align-middle">
                      <thead>
                        <tr>
                          <th>Task ID</th>
                          <th>Title</th>
                          <th>Description</th>
                          <th>Images</th>
                          <th>Priority</th>
                          <th>Status</th>
                          <th>Created By</th>
                          <th>Created At</th>
                          <th>Comments</th>
                        </tr>
                      </thead>
                      <tbody>
                        {doneList.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="text-center">
                              No done tasks found
                            </td>
                          </tr>
                        ) : (
                          doneList.map((item) => (
                            <React.Fragment key={item.id}>
                              <tr>
                                <td>{item.id}</td>
                                <td>{item.title}</td>
                                <td style={{ maxWidth: 280 }}>
                                  {item.description ? (
                                    <div
                                      className="rich-text-content"
                                      dangerouslySetInnerHTML={{
                                        __html: item.description,
                                      }}
                                    />
                                  ) : (
                                    "-"
                                  )}
                                </td>
                                <td>
                                  <div className="d-flex flex-wrap gap-1">
                                    {(item.images || []).map((img) => (
                                      <a
                                        key={img}
                                        href={getImageUrl(img)}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        <img
                                          src={getImageUrl(img)}
                                          alt={img}
                                          style={{
                                            width: 48,
                                            height: 48,
                                            objectFit: "cover",
                                            borderRadius: 4,
                                          }}
                                        />
                                      </a>
                                    ))}
                                    {(!item.images || item.images.length === 0) &&
                                      "-"}
                                  </div>
                                </td>
                                <td>
                                  <span
                                    className={`badge fs-5 px-2 py-1 ${getPriorityBadgeClass(item)}`}
                                  >
                                    {getPriorityLabel(item)}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    className={`badge fs-5 px-2 py-1 ${getStatusBadgeClass(item)}`}
                                  >
                                    {getStatusLabel(item)}
                                  </span>
                                </td>
                                <td>{item.created_by_name || "-"}</td>
                                <td>
                                  {item.created_at
                                    ? new Date(item.created_at).toLocaleString()
                                    : "-"}
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => toggleComments(item.id)}
                                  >
                                    {expandedTaskId === item.id ? "Hide" : "View"}{" "}
                                    ({item.comment_count ||
                                      item.comments?.length ||
                                      0})
                                  </button>
                                </td>
                              </tr>
                              {expandedTaskId === item.id && (
                                <tr>
                                  <td colSpan={9} className="bg-light">
                                    <div className="p-3">
                                      <h6 className="mb-3">Comments</h6>
                                      {(item.comments || []).length === 0 ? (
                                        <p className="text-muted mb-0">
                                          No comments yet.
                                        </p>
                                      ) : (
                                        <ul className="list-unstyled mb-0">
                                          {(item.comments || []).map((c) => (
                                            <li
                                              key={c.id}
                                              className="border rounded p-2 mb-2 bg-white"
                                            >
                                              <div className="d-flex justify-content-between flex-wrap gap-1 mb-1">
                                                <strong>
                                                  {c.user_name || "Unknown user"}
                                                </strong>
                                                <small className="text-muted">
                                                  {formatCommentDate(c.created_at)}
                                                </small>
                                              </div>
                                              <div
                                                className="mb-0 rich-text-content"
                                                dangerouslySetInnerHTML={{
                                                  __html: c.comment,
                                                }}
                                              />
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="row align-items-center mt-3">
                    <div className="col-sm-6">
                      Showing {start} to {end} of {totalItems} entries
                    </div>
                    <div className="col-sm-6">
                      {totalPages > 0 && (
                        <PaginationComponent
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={(page) => setCurrentPage(page)}
                        />
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DoneTaskList;
